import classnames from 'classnames';
import type { ComponentProps, ReactNode, UIEventHandler } from 'react';
import {
  Children,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'react-use';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Dots } from '#src/atoms/navigation/Dots/index.tsx';
import { WithScrollButtons } from '#src/atoms/navigation/WithScrollButtons/index.tsx';
import { range } from '#src/lib/arrays.ts';
import { unitMapping, type Unit } from '#src/lib/style.ts';
import { desktopAndAbove } from '#src/style/mediaQuery.ts';
import { hideScrollbar } from '#src/style/utils.tsx';

const DotsContainer = styled.div`
  position: absolute;
  left: 50%;
  bottom: var(--dots-bottom, var(--half-unit));
  z-index: 1;
  transform: translateX(-50%);
`;

const Row = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollableContent = styled.div<{
  itemToDisplay: number;
  withMask?: boolean;
  maskSize: number;
  shouldOverhang?: boolean;
  overhang: Unit;
  itemGap: Unit;
}>`
  --visible-vertical-overflow-size: 0px;
  --mask-size: calc(${({ maskSize }) => maskSize} * var(--unit));
  --item-gap: ${({ itemGap }) => unitMapping[itemGap]};
  --overhang: ${({ overhang }) => unitMapping[overhang]};
  --final-overhang: ${({ shouldOverhang }) =>
    shouldOverhang ? 'var(--overhang)' : '0px'};
  --item-wrapper-width: calc(
    (
        (100% - var(--final-overhang)) - var(--item-gap) *
          ${({ itemToDisplay }) => itemToDisplay - 1}
      ) /
      ${({ itemToDisplay }) => itemToDisplay}
  );
  display: flex;
  scroll-snap-type: x mandatory;
  gap: var(--item-gap);

  overflow-x: auto;
  overflow-y: hidden;

  /* With this negative margin technique, Scrollable won't work with flex-direction: row-reverse
   * See https://gitlab.com/sorare/frontend/-/merge_requests/15065
   */
  /*
   * Also, the negative margin/positive padding trick is used here to
   * let the content be visible even with the overflow-y: hidden;
   * See https://stackoverflow.com/questions/18135204/setting-overflow-y-causes-overflow-x-to-change-as-well
   *
   * The horizontal bleed snaps items to the viewport center. Disable it with
   * the noFullBleed class when the Scrollable lives in a narrow/offset
   * container that should keep items snapped to itself rather than the viewport.
   */
  margin: calc(-1 * var(--visible-vertical-overflow-size))
    clamp(-50%, calc(50% - calc(50 * var(--vw))), 0px);
  padding: var(--visible-vertical-overflow-size)
    clamp(0px, calc(calc(50 * var(--vw)) - 50%), 50%);
  ${hideScrollbar}

  &.noFullBleed {
    margin-inline: 0;
    padding-inline: 0;
  }

  @media ${desktopAndAbove} {
    &.withMask {
      padding-inline: var(--mask-size);
      scroll-padding-inline: var(--mask-size);
      margin-inline: calc(-1 * var(--mask-size));
      mask-image: linear-gradient(
        to right,
        transparent,
        white var(--mask-size),
        white calc(100% - var(--mask-size)),
        transparent
      );
    }
  }
`;

const ItemWrapper = styled.div`
  scroll-snap-align: center;
  scroll-snap-stop: always;
  min-width: var(--item-wrapper-width, 100%);
  max-width: var(--item-wrapper-width, 100%);

  &:empty {
    display: none;
  }
`;
type CommonProps = {
  children?: ReactNode;
  itemToDisplay: number;
  withMask?: boolean;
  contained?: ComponentProps<typeof WithScrollButtons>['contained'];
  /**
   * In a mobile viewport when there are multiple items to display, show a
   * partial edge of the next offscreen item to indicate to the user that
   * there are more items to scroll to (since no arrow buttons shown in mobile).
   */
  overhang?: boolean | Unit;
  itemGap?: Unit;
  scrollButtonsProps?: Partial<ComponentProps<typeof WithScrollButtons>>;
  dots?: string[];
  dotsVariant?: ComponentProps<typeof Dots>['variant'];
  visibleVerticalOverflowSize?: number;
  fullBleed?: boolean;
  withScrollButtons?: boolean;
  onScroll?: (x: number) => void;
  onFirstVisibleIndexChanged?: (i: number) => void;
};
type ControlledProps = CommonProps & {
  indexToScroll: number;
  onVisibleItemsChanged?: (items: number[]) => void;
};
type UnControlledProps = CommonProps & {
  indexToScroll?: never;
  onVisibleItemsChanged?: (items: number[]) => void;
};

type Props = ControlledProps | UnControlledProps;

export const Scrollable = ({
  children,
  withMask,
  itemToDisplay,
  indexToScroll,
  onScroll,
  onVisibleItemsChanged,
  onFirstVisibleIndexChanged,
  contained,
  overhang,
  itemGap = 2,
  withScrollButtons = true,
  scrollButtonsProps,
  dots,
  dotsVariant,
  visibleVerticalOverflowSize = 0,
  fullBleed = true,
}: Props) => {
  const numberOfItems = Children.toArray(children).length;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const manualScroll = useRef(false);
  const [visibleIndexes, setVisibleIndexes] = useState(range(itemToDisplay));
  const containerRef = useRef<HTMLDivElement>(null);
  const [firstVisibleIndex, _setFirstVisibleIndex] = useState(
    visibleIndexes[0]
  );
  const scrollToIndex = useCallback((index: number) => {
    if (!containerRef.current) {
      return;
    }
    const selectedItem = containerRef.current.childNodes[index];
    if (selectedItem instanceof HTMLElement) {
      const scrollValue = selectedItem.offsetLeft;
      containerRef.current?.scroll({
        left: scrollValue,
        behavior: 'smooth',
      });
    }
  }, []);

  let overhangSize: Unit = 0;
  if (overhang) {
    if (typeof overhang === 'number') {
      overhangSize = overhang;
    } else {
      overhangSize = 2;
    }
  }

  const setFirstVisibleIndex = (i: number) => {
    _setFirstVisibleIndex(i);
    onFirstVisibleIndexChanged?.(i);
  };

  const detectVisibleIndexes: UIEventHandler<HTMLDivElement> = e => {
    const { scrollLeft, children: child } = e.target as HTMLDivElement;
    const itemWidth = child[0]?.clientWidth;

    if (typeof scrollLeft !== 'number' || typeof itemWidth !== 'number') {
      return;
    }

    const firstVisibleItem = Math.round(scrollLeft / (itemWidth + itemGap));
    const indexes = range(itemToDisplay).map(i =>
      Math.min(firstVisibleItem + i, Children.count(children) - 1)
    );
    setVisibleIndexes(indexes);
    if (!manualScroll.current) {
      setFirstVisibleIndex(Math.ceil(firstVisibleItem / itemToDisplay));
    }
  };

  useLayoutEffect(() => {
    if (typeof indexToScroll === 'number') {
      scrollToIndex(indexToScroll);
    }
  }, [scrollToIndex, indexToScroll]);

  useDebounce(() => onVisibleItemsChanged?.(visibleIndexes), 50, [
    visibleIndexes,
  ]);

  const to = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (containerRef.current) {
      const callback = () => {
        manualScroll.current = false;
      };
      if ('onscrollend' in window) {
        containerRef.current.onscrollend = callback;
      } else {
        containerRef.current.onscroll = () => {
          clearTimeout(to.current);
          to.current = setTimeout(callback, 100);
        };
      }
    }
    return () => {
      clearTimeout(to.current);
    };
  }, []);

  const content = (
    <Row>
      <ScrollableContent
        className={classnames({ withMask, noFullBleed: !fullBleed })}
        itemToDisplay={itemToDisplay}
        ref={containerRef}
        onScroll={e => {
          if (containerRef.current) {
            onScroll?.(containerRef.current.scrollLeft);
          }
          detectVisibleIndexes(e);
        }}
        role="navigation"
        shouldOverhang={!!overhang && numberOfItems > itemToDisplay}
        overhang={overhangSize}
        itemGap={itemGap}
        maskSize={5}
        style={
          {
            '--visible-vertical-overflow-size': `${visibleVerticalOverflowSize}px`,
          } as CSSProperties
        }
      >
        {Children.toArray(children).map((child, index) => (
          // oxlint-disable-next-line react/no-array-index-key
          <ItemWrapper key={index}>{child}</ItemWrapper>
        ))}
      </ScrollableContent>

      {numberOfItems > itemToDisplay && !!dots && (
        <DotsContainer>
          <Dots
            titles={
              itemToDisplay === 1
                ? dots
                : dots.filter((_, i) => i % (itemToDisplay - 1))
            }
            selectedIndex={firstVisibleIndex}
            variant={dotsVariant}
            setSelectedIndex={index => {
              manualScroll.current = true;
              setFirstVisibleIndex(index);
              if (itemToDisplay === 1) {
                scrollToIndex(index);
              } else {
                scrollToIndex(index * (itemToDisplay - 1));
              }
            }}
          />
        </DotsContainer>
      )}
    </Row>
  );

  if (withScrollButtons) {
    return (
      <WithScrollButtons
        contained={contained}
        wrapperRef={wrapperRef}
        scrollContainerRef={containerRef}
        itemGap={itemGap}
        overhangSize={overhangSize}
        {...scrollButtonsProps}
      >
        {content}
      </WithScrollButtons>
    );
  }
  return content;
};
